import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, Sparkles, MessageCircle, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/trackEvent";

/** Routes with their own dedicated chat/admin surfaces. */
const HIDDEN_PREFIXES = ["/buddy", "/admin"];

export const OPEN_CHAT_EVENT = "gotocoins:open-chat";

/**
 * Single floating action button (bottom-right) replacing the previous
 * review / chat / share floats. Expands into three real actions.
 */
export function FloatingActionMenu() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const share = async () => {
    const url = window.location.href;
    const title = document.title;
    trackEvent("share_clicked", { source: "floating_menu", path: location.pathname });
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch {
      /* user dismissed the share sheet */
    }
  };

  const actions = [
    {
      label: "Review a Coin",
      icon: Sparkles,
      run: () => {
        trackEvent("coin_review_started", { source: "floating_menu" });
        navigate("/coin-reviewer");
      },
    },
    {
      label: "Ask Crypto AI",
      icon: MessageCircle,
      run: () => window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT, { detail: "floating_menu" })),
    },
    { label: "Share", icon: Share2, run: share },
  ];

  return (
    <div
      ref={ref}
      className="fixed bottom-3 right-3 md:bottom-6 md:right-6 z-[55] flex flex-col items-end gap-2"
    >
      {open &&
        actions.map((a, i) => (
          <button
            key={a.label}
            type="button"
            onClick={() => {
              setOpen(false);
              a.run();
            }}
            style={{ animationDelay: `${i * 40}ms` }}
            className="animate-fade-in inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/95 backdrop-blur px-4 py-2.5 text-sm font-semibold shadow-xl hover:bg-secondary transition-colors"
          >
            <a.icon className="h-4 w-4 text-primary shrink-0" />
            {a.label}
          </button>
        ))}

      <button
        type="button"
        aria-expanded={open}
        aria-label={open ? "Close quick actions" : "Open quick actions"}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center justify-center h-12 w-12 rounded-full",
          "bg-gradient-to-r from-primary to-purple-500 text-primary-foreground",
          "shadow-2xl transition-transform hover:scale-105",
        )}
      >
        {open ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
      </button>
    </div>
  );
}

export default FloatingActionMenu;
